import {
  ChangeDetectorRef,
  Component,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';

import { AppState } from 'src/app/core/state/app.state';
import { FormProperty } from '../../../../core/models/form.models';
import { FormsService } from 'src/app/core/services/forms.service';
import { LoggerService } from '@services/logger.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { WidgetSettingsComponent } from '../widget-settings/widget-settings.component';
import { WidgetSettingsService } from 'src/app/widgets/services/widget.settings.service';
import { DynamicFormComponent } from '../../../../shared/components/dynamic-form/dynamic-form.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

@Component({
  selector: 'app-widget-settings-data-series',
  templateUrl: './widget-settings-data-series.component.html',
  styleUrls: ['./widget-settings-data-series.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
  ],
})
export class WidgetSettingsDataSeriesComponent extends WidgetSettingsComponent {
  constructor(
    protected formService: FormsService,
    protected widgetSettings: WidgetSettingsService,
    protected cdf: ChangeDetectorRef,
    private translateService: TranslateService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
  ) {
    super(formService, widgetSettings, cdf, store, logger);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.formService.populateFormValues(this.formGroup, this.widget, (vals) => {
      const colorArray = Array.isArray(vals['settings.slice_color_id'])
        ? vals['settings.slice_color_id']
        : [];

      const result = { ...vals };
      // Expand array values into dot-notation keys
      colorArray.forEach((val, i) => {
        result[`settings.slice_color_id.${i}`] = val;
      });

      delete result['settings.slice_color_id'];

      return result;
    });
  }

  formProperties: FormProperty[] = [
    {
      propertyName: 'settings.series.color_source',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-color-source',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.settingsColorSchemeOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.fill_opacity',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-fill-opacity',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.fillOpacityOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.show_series_markers',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.display-series-markers',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      controlType: 'select',
      columnIndex: 1,
      valueOptions: this.formService.autoYesNoOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.series.line_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-line-width',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.series.border_width',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-border-width',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.series.border_radius',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-border-radius',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      controlType: 'input-number',
      columnIndex: 1,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.series.border_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-border-color',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-series-design-settings',
        style: 'data-series-design-settings',
      },
      columnIndex: 1,
      controlType: 'colorpicker',
      isMassUpdate: true,
    },

    // DATA LABELS
    {
      propertyName: 'settings.show_data_labels',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.show-data-labels',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'select',
      columnIndex: 0,
      valueOptions: this.formService.autoYesNoOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.format',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-format',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      columnIndex: 1,
      controlType: 'select',
      valueOptions: this.formService.dataLabelsFormatOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-rotation',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'input-number',
      columnIndex: 0,
      metricSymbol: 'degrees °',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.percent_field',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.series-percent-estimation-logic',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'select',
      columnIndex: 1,
      valueOptions: this.formService.dataLabelsPercentFieldOptions,
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.inside',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.position-data-labels-inside',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.dataLabelsPlacementOptions,
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.distance',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-distance-from-series',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'input-number',
      additionalConfig: { min: -100, max: 100 },
      metricSymbol: 'px',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.x_offset',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-x-offset',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'input-number',
      additionalConfig: { min: -100, max: 100 },
      metricSymbol: 'px',
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.y_offset',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-y-offset',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'input-number',
      additionalConfig: { min: -100, max: 100 },
      metricSymbol: 'px',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-font-size',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.fontSizeOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.align',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-align',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.itemHorizontalAlignOptions,
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.padding_left',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-left-padding',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.padding_right',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-right-padding',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.background_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-background-color',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      columnIndex: 0,
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.data_labels.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.data-labels-text-color',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.data-labels-settings',
        style: 'data-labels-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },

    // STACK LABELS
    {
      propertyName: 'settings.show_stack_labels',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.show-stack-labels',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.stackLabelsFormatOptions,
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.format',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-format',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.stackLabelsFormatOptions,
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.rotation',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-rotation',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'degrees °',
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.inside',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.position-stack-labels-inside',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.dataLabelsPlacementOptions,
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.x_offset',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-x-offset',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'input-number',
      additionalConfig: { min: -100, max: 100 },
      metricSymbol: 'px',
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.y_offset',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-y-offset',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      columnIndex: 1,
      controlType: 'input-number',
      additionalConfig: { min: -100, max: 100 },
      metricSymbol: 'px',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.font_size',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-font-size',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'select',
      valueOptions: this.formService.fontSizeOptions,
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.align',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-align',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'select',
      columnIndex: 1,
      valueOptions: this.formService.itemHorizontalAlignOptions,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.padding_left',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-left-padding',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      columnIndex: 0,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.padding_right',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-right-padding',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'input-number',
      metricSymbol: 'px',
      columnIndex: 1,
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.background_color',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-background-color',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      columnIndex: 0,
      controlType: 'colorpicker',
      isMassUpdate: true,
    },
    {
      propertyName: 'settings.stack_labels.color',
      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.stack-labels-text-color',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.stack-labels-settings',
        style: 'stack-labels-settings',
      },
      controlType: 'colorpicker',
      columnIndex: 1,
      isMassUpdate: true,
    },

    {
      propertyName: 'settings.slice_color_id', // main property to hold the array of colors
      // this will create additional controls like settings.slice_color_id.0, settings.slice_color_id.1, etc.
      // for each color in the array

      propertyLabel: this.translateService.instant(
        'widget-settings-data-series.label.slice-color',
      ),
      propertyGroup: {
        key: 'widget-settings-data-series.group.slice-colors',
        style: 'stack-labels-settings',
      },
      controlType: 'colorpicker-array',
      defaultValue: [], // optional
      additionalConfig: {
        // Drive how many pickers to show
        lengthFrom: 'settings.number_of_items', // another flat control you already have
        defaultColor: null, // default color for new items, transparent if not set
        ngxColors: {
          // forward any ngx-colors inputs if you like
          // format: 'hex', theme: 'light', position: 'bottom'
        },
      },
      shouldDisplay: () => true,
    },
  ];
}
